import { useState } from 'react'
import { CircleHelpIcon } from 'lucide-react'
import type { Issue, CategorySummaries } from '@/types/risk_score'
import { track } from '@/lib/track'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { CategorySection, CATEGORY_ITEMS } from './CategorySection'

const FILTER_BTN_CLASS = 'py-1 px-3.25 text-[11px] font-semibold rounded-full border border-(--bp-border) bg-(--bp-surface) text-(--bp-ink3) cursor-pointer'
const FILTER_BTN_ACTIVE_CLASS = 'bg-(--bp-ink) text-white border-(--bp-ink)'

function normalizeCategory (category: string | undefined): typeof CATEGORY_ITEMS[number]['key'] {
  const found = CATEGORY_ITEMS.find(item => item.key === category)
  return (found != null) ? found.key : 'other'
}

export function RisksTab ({
  issues,
  categorySummaries,
  criticalCount,
  publicFunnelEnabled,
  projectId,
  onOpenHelp
}: {
  issues: { critical: Issue[], warning: Issue[], info: Issue[] } | undefined
  categorySummaries: CategorySummaries | undefined
  criticalCount: number
  publicFunnelEnabled: boolean
  projectId: number
  onOpenHelp: () => void
}): React.ReactElement {
  // WARNING/INFO（参考所見・確信度が低い指摘）は初期状態では隠し、ボタン操作で確認できるようにする。
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all')

  if (issues == null) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--bp-ink3)', fontSize: 13 }}>
        リスクデータがありません
      </div>
    )
  }

  const allIssues = [
    ...(issues.critical ?? []).map(i => ({ issue: i, severity: 'critical' as const })),
    ...(issues.warning ?? []).map(i => ({ issue: i, severity: 'warning' as const })),
    ...(issues.info ?? []).map(i => ({ issue: i, severity: 'info' as const }))
  ]
  const filtered = filter === 'all' ? allIssues : allIssues.filter(i => i.severity === filter)

  return (
    <div>
      <div className='flex items-center gap-1.5 flex-wrap mb-4'>
        <span className='text-[11px] font-semibold text-(--bp-ink3) mr-1'>表示:</span>
        <button className={cn(FILTER_BTN_CLASS, filter === 'all' && FILTER_BTN_ACTIVE_CLASS)} onClick={() => setFilter('all')}>すべて <strong>{allIssues.length}</strong>
        </button>
        <button className={cn(FILTER_BTN_CLASS, filter === 'critical' && FILTER_BTN_ACTIVE_CLASS)} onClick={() => setFilter('critical')}>CRITICAL <strong>{issues.critical?.length}</strong>
        </button>
        <button className={cn(FILTER_BTN_CLASS, filter === 'warning' && FILTER_BTN_ACTIVE_CLASS)} onClick={() => setFilter('warning')}>WARNING <strong>{issues.warning?.length}</strong>
        </button>
        <button className={cn(FILTER_BTN_CLASS, filter === 'info' && FILTER_BTN_ACTIVE_CLASS)} onClick={() => setFilter('info')}>INFO <strong>{issues.info?.length}</strong>
        </button>
        <Button variant='ghost' size='sm' className='ml-auto' onClick={onOpenHelp}>
          <CircleHelpIcon size={14} /> 使い方
        </Button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--bp-ink3)', lineHeight: 1.6, margin: '10px 0 16px' }}>
        この診断は入力されたヒアリング内容をもとにした一次判定です。情報が少ない場合は誤検知の可能性があります。詳しくは専門家にご相談ください。
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {CATEGORY_ITEMS.map(item => {
          const categoryAllIssues = allIssues.filter(i => normalizeCategory(i.issue.category) === item.key)
          const categoryFilteredIssues = filtered.filter(i => normalizeCategory(i.issue.category) === item.key)
          return (
            <CategorySection
              key={item.key}
              item={item}
              issues={categoryFilteredIssues}
              allIssues={categoryAllIssues}
              activeFilter={filter}
              summary={categorySummaries?.[item.key]}
              defaultOpen={false}
            />
          )
        })}
        {filtered.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px 0',
            color: 'var(--bp-ink3)',
            fontSize: 13
          }}
          >該当するリスクはありません
          </div>
        )}
      </div>

      {publicFunnelEnabled && criticalCount > 0 && (
        <div style={{
          marginTop: 24,
          padding: '20px 24px',
          background: 'linear-gradient(135deg,#1A0500,#3A1200)',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16
        }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#FFCCB0', marginBottom: 5 }}>
              CRITICAL {criticalCount}件を放置したまま開発に進みますか？
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,204,176,.75)', lineHeight: 1.65 }}>
              未解決のまま実装を始めると、炎上リスクが極めて高くなります。シニアアーキテクトに相談して、仕様を堅牢にしませんか？
            </div>
          </div>
          <a
            href='/contact'
            onClick={() => track('cta_click', 'risk_consult', { project_id: projectId })}
            style={{
              padding: '11px 22px',
              background: 'var(--bp-cta)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--bp-font-sans)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            要件定義を相談する →
          </a>
        </div>
      )}
    </div>
  )
}
